import { forwardRef } from 'react'
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import { cn } from '@/lib/utils'
import { usePortalContainer, useOverlayLayer } from './portal-container'
import { placed, placedChild } from '@/lib/style-guard'

const DropdownMenu = DropdownMenuPrimitive.Root
/* The trigger's look is named here (30.10.2026, two pages styled it by hand): `inline` is a quiet
   text trigger in a card's meta line, `icon` a quiet 44px ⋯ button. Without a look it is unstyled,
   for `asChild` use with a Button. */
const TRIGGER_INLINE = 'flex min-h-11 items-center gap-1 rounded-[var(--radius-sm)] px-2 -mx-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] cursor-pointer focus-ring'
const TRIGGER_LOOK = {
  inline: `${TRIGGER_INLINE} text-sm`,
  inlineSmall: `${TRIGGER_INLINE} text-small`,
  icon: 'focus-ring inline-flex size-11 items-center justify-center rounded-[var(--radius-md)] text-[var(--text-secondary)] hover:bg-[var(--surface-muted)] hover:text-[var(--text-primary)] transition-colors cursor-pointer',
} as const
const DropdownMenuTrigger = forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger> & { look?: keyof typeof TRIGGER_LOOK }
>(({ className, look, ...props }, ref) => {
  // With a look, `asChild` merges it into the child: the child's own classes are a caller's.
  if (look && props.asChild) placedChild(props.children, 'DropdownMenuTrigger')
  return <DropdownMenuPrimitive.Trigger ref={ref} className={look ? cn(TRIGGER_LOOK[look], placed(className, 'DropdownMenuTrigger', props)) : placed(className, 'DropdownMenuTrigger', props)} {...props} />
})
DropdownMenuTrigger.displayName = 'DropdownMenuTrigger'
const DropdownMenuGroup = DropdownMenuPrimitive.Group

const DropdownMenuContent = forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & {
    /** Portal mount node; defaults to the brand-scoped app shell (white-label safe). */
    container?: HTMLElement | null
  }
>(({ className, sideOffset = 8, container, ...props }, ref) => {
  placedChild(props.asChild ? props.children : null, 'DropdownMenuContent')
  const ctxContainer = usePortalContainer()
  const layer = useOverlayLayer('menu')
  return (
  <DropdownMenuPrimitive.Portal container={container ?? ctxContainer ?? undefined}>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-[var(--z-dropdown)] min-w-[180px] overflow-hidden',
        'rounded-[var(--radius-lg)] border border-[var(--border-default)]',
        'bg-[var(--surface-elevated)] p-1',
        'shadow-[var(--shadow-lg)]',
        'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
        'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
        placed(className, 'DropdownMenuContent', props)
      )}
      {...props}
      style={{ ...props.style, ...layer }}
    />
  </DropdownMenuPrimitive.Portal>
  )
})
DropdownMenuContent.displayName = 'DropdownMenuContent'

const DropdownMenuItem = forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    destructive?: boolean
    /** The forward action in a menu (move to the next stage). */
    brand?: boolean
  }
>(({ className, destructive, brand, ...props }, ref) => {
  placedChild(props.asChild ? props.children : null, 'DropdownMenuItem')
  return (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex items-center gap-2 rounded-[var(--radius-md)] px-2 py-1.5',
      'text-label cursor-pointer select-none outline-none',
      'transition-colors duration-[var(--duration-fast)]',
      destructive
        ? 'text-[var(--status-error-text)] focus:bg-[var(--status-error-bg)] focus:text-[var(--status-error-text)]'
        : brand
          ? 'text-[var(--icon-primary)] focus:bg-[var(--surface-muted)]'
          : 'text-[var(--text-primary)] focus:bg-[var(--surface-muted)]',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      '[&_svg]:size-4 [&_svg]:shrink-0',
      placed(className, 'DropdownMenuItem', props)
    )}
    {...props}
  />
  )
})
DropdownMenuItem.displayName = 'DropdownMenuItem'

const DropdownMenuSeparator = forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => {
  placedChild(props.asChild ? props.children : null, 'DropdownMenuSeparator')
  return (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-[var(--border-default)]', placed(className, 'DropdownMenuSeparator', props))}
    {...props}
  />
  )
})
DropdownMenuSeparator.displayName = 'DropdownMenuSeparator'

const DropdownMenuLabel = forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>
>(({ className, ...props }, ref) => {
  placedChild(props.asChild ? props.children : null, 'DropdownMenuLabel')
  return (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn('px-2 py-1.5 text-caption text-[var(--text-muted)]', placed(className, 'DropdownMenuLabel', props))}
    {...props}
  />
  )
})
DropdownMenuLabel.displayName = 'DropdownMenuLabel'

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuGroup,
}
